import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, isRef as _isRef, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, mergeProps as _mergeProps, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "payments-tab" }
const _hoisted_2 = { class: "payments-tab__toolbar" }
const _hoisted_3 = { class: "payments-tab__toolbar-main" }
const _hoisted_4 = { class: "payments-tab__toolbar-controls" }
const _hoisted_5 = { class: "payments-tab__toolbar-filters" }
const _hoisted_6 = { class: "payments-tab__toolbar-actions" }

import {
    computed, onMounted, onUnmounted, ref, watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import type { DataTablePageEvent, DataTableSortEvent } from 'primevue';
import Column from 'primevue/column';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import TabStatPills from '@/components/ui/TabStatPills.vue';
import TabSearch from '@/components/ui/TabSearch.vue';
import PaymentBankFilter from '@/components/payments/BankAccountFilter.vue';
import PaymentTrustedAccountFilter from '@/components/payments/TrustedAccountFilter.vue';
import CheckboxFilter from '@/components/ui/CheckboxFilter.vue';
import DateRangePicker from '@/components/ui/DateRangePicker.vue';
import AppTable, {
    type AppTableInstance,
    col,
} from '@/components/ui/AppTable.vue';
import ColumnCustomizeControl from '@/components/ui/ColumnCustomizeControl.vue';
import useTableColumns from '@/composables/useTableColumns';
import { savePaymentBankIds } from '@/composables/usePaymentBankOptions';
import { savePaymentTrustedAccountIds } from '@/composables/usePaymentTrustedAccountOptions';
import IconExport from '@/components/icons/IconExport.vue';
import ToolbarIconButton from '@/components/ui/ToolbarIconButton.vue';
import QueuedExportModal from '@/components/modals/QueuedExportModal.vue';
import RejectPaymentModal from '@/components/modals/RejectPaymentModal.vue';
// PHASE2: import ModifyPaymentModal from '@/components/modals/ModifyPaymentModal.vue';
import PaymentsEmptyState from '@/components/payments/PaymentsEmptyState.vue';
import SsnDisplay from '@/components/ui/SsnDisplay.vue';
import {
    PAYMENTS_COLUMNS,
    PAYMENTS_LOCKED_COLUMN_KEYS,
    PAYMENTS_SORTABLE_KEYS,
    type PaymentRow,
    usePayments,
} from '@/composables/usePayments';
import {
    exportPayments,
    mapTableColumnsToExportColumns,
} from '@/services/paymentService';
import {
    PaymentStatus,
    parseStatusQuery,
    type PaymentStatusValue,
} from '@/services/paymentStatus';
import { ApiError } from '@/models/errors/ApiError';
import { formatDisplayDate } from '@/utils/dt';
import { useAuthStore } from '@/stores/auth';
import { useTableColumnPreferencesStore } from '@/stores/tableColumnPreferences';
import { TableColumnPreferenceId } from '@/services/tableColumnPreferenceService';

type PaymentActionMode = 'none' | 'dynamic' | 'reject' | 'modify';
type ProcessDateMode = 'cleared-aware' | 'process-date';

const QUEUED_EXPORT_TITLE = 'Payments export started';
const DEFAULT_QUEUED_EXPORT_MESSAGE = 'Your export is in progress. You’ll get an email with a download link shortly.';
const EXPORT_QUEUED_COOLDOWN_MS = 60_000;

interface Props {
    /** Statuses this tab requests while the status filter is empty; omit to request every status. */
    baseStatuses?: PaymentStatusValue[];
    /** Statuses selected on first load when the URL does not preselect another valid status. */
    defaultStatuses?: PaymentStatusValue[];
    /** Statuses the status filter offers; omit to hide the filter. */
    statusOptions?: PaymentStatusValue[];
    emptyDescription: string;
    processDateHeader?: string;
    processDateMode?: ProcessDateMode;
    actionMode?: PaymentActionMode;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PaymentsTableTab',
  props: {
    baseStatuses: {},
    defaultStatuses: {},
    statusOptions: {},
    emptyDescription: {},
    processDateHeader: { default: 'Process Date' },
    processDateMode: { default: 'cleared-aware' },
    actionMode: { default: 'none' }
  },
  setup(__props: any) {

/* eslint-env vue/setup-compiler-macros */
const STATUS_LABEL: Record<PaymentStatusValue, string> = {
    [PaymentStatus.OPEN]: 'Open',
    [PaymentStatus.PENDING]: 'Pending',
    [PaymentStatus.LOW_BALANCE]: 'Low Balance',
    [PaymentStatus.CANCEL]: 'Cancelled',
    [PaymentStatus.CLEARED]: 'Cleared',
};

const props = __props;

const tableRef = ref<AppTableInstance | null>(null);
const rejectModalVisible = ref(false);
// PHASE2: const modifyModalVisible = ref(false);
const selectedPaymentId = ref<number | null>(null);
const exporting = ref(false);
const exportCooldownActive = ref(false);
const exportQueuedModalVisible = ref(false);
const exportQueuedTitle = ref(QUEUED_EXPORT_TITLE);
const exportQueuedMessage = ref(DEFAULT_QUEUED_EXPORT_MESSAGE);
const toast = useToast();
const authStore = useAuthStore();
const preferenceStore = useTableColumnPreferencesStore();
const {
    payments: paymentColumnPreference,
    loadedUserId: preferencesLoadedUserId,
    loading: preferencesLoading,
    saving: preferencesSaving,
    resetting: preferencesResetting,
} = storeToRefs(preferenceStore);
let exportCooldownTimer: ReturnType<typeof setTimeout> | null = null;

const route = useRoute();

// A dashboard or report deep link preselects statuses this tab actually offers.
const routeStatuses = parseStatusQuery(route.query.status, props.statusOptions ?? []);
// Dashboard/report deep links are explicit filters; a column preference must not broaden them.
const hasRouteStatusFilter = routeStatuses.length > 0;
const defaultStatuses = parseStatusQuery(
    props.defaultStatuses ?? [],
    props.statusOptions ?? [],
);
const selectedStatuses = ref<PaymentStatusValue[]>(
    routeStatuses.length > 0 ? routeStatuses : defaultStatuses,
);

const effectiveStatuses = computed<PaymentStatusValue[] | undefined>(() => (
    selectedStatuses.value.length > 0 ? selectedStatuses.value : props.baseStatuses
));

const statusOptionLabels = computed(
    () => (props.statusOptions ?? []).map((status) => STATUS_LABEL[status]),
);

const selectedStatusLabels = computed<string[]>({
    get: () => selectedStatuses.value.map((status) => STATUS_LABEL[status]),
    set: (labels) => {
        selectedStatuses.value = (props.statusOptions ?? []).filter(
            (status) => labels.includes(STATUS_LABEL[status]),
        );
    },
});

// PHASE1: Pending-only tabs do not have a Bank value to display or filter.
const showsBank = (
    props.baseStatuses === undefined
    || props.baseStatuses.includes(PaymentStatus.CLEARED)
);
const disabledColumnKeys = showsBank ? [] : ['bank'];

const {
    rows,
    loading,
    hasExactTotals,
    totalRecords,
    stats,
    search,
    // PHASE1: Filter Payments by the displayed Bank column.
    bankIds,
    payeeIds,
    dateRange,
    // PHASE2: bankingAccountIds,
    page,
    limit,
    sortField,
    sortOrder,
    fetchPayments,
    getExportParams,
} = usePayments(effectiveStatuses, showsBank);

const {
    visibleColumnKeys,
    columnOrder,
    orderedVisibleColumnKeys,
    applySettings: applyColumnSettings,
} = useTableColumns(PAYMENTS_COLUMNS, PAYMENTS_LOCKED_COLUMN_KEYS);

const displayedColumnKeys = computed(() => (
    showsBank
        ? orderedVisibleColumnKeys.value
        : orderedVisibleColumnKeys.value.filter((key) => key !== 'bank')
));
const displayedColumnKeySet = computed(() => new Set(displayedColumnKeys.value));
const showBankFilter = computed(() => (
    showsBank && displayedColumnKeySet.value.has('bank')
));
const showTrustedAccountFilter = computed(() => (
    displayedColumnKeySet.value.has('trustedAccount')
));
const showStatusFilter = computed(() => (
    statusOptionLabels.value.length > 0 && displayedColumnKeySet.value.has('status')
));
const columnPreferencesReady = computed(() => {
    const userId = authStore.user?.id;
    return userId !== undefined
        && preferencesLoadedUserId.value === userId
        && !preferencesLoading.value;
});
const columnPreferencesBusy = computed(() => (
    preferencesSaving.value || preferencesResetting.value
));

function resetFiltersForHiddenColumns() {
    if (showsBank && !showBankFilter.value && bankIds.value !== null) {
        bankIds.value = null;
        savePaymentBankIds(null);
    }
    if (!showTrustedAccountFilter.value && payeeIds.value !== null) {
        payeeIds.value = null;
        savePaymentTrustedAccountIds(null);
    }
    if (
        !showStatusFilter.value
        && statusOptionLabels.value.length > 0
        && !hasRouteStatusFilter
    ) {
        selectedStatuses.value = [];
    }
}

watch(paymentColumnPreference, (preference) => {
    applyColumnSettings(preference);
    resetFiltersForHiddenColumns();
}, { immediate: true, deep: true });

watch(() => authStore.user?.id, (userId) => {
    if (userId === undefined) {
        preferenceStore.clear();
        return;
    }
    preferenceStore.load(userId);
}, { immediate: true });

onMounted(() => fetchPayments());

onUnmounted(() => {
    if (exportCooldownTimer !== null) {
        clearTimeout(exportCooldownTimer);
    }
});

function startExportCooldown() {
    if (exportCooldownTimer !== null) {
        clearTimeout(exportCooldownTimer);
    }

    exportCooldownActive.value = true;
    exportCooldownTimer = setTimeout(() => {
        exportCooldownActive.value = false;
        exportCooldownTimer = null;
    }, EXPORT_QUEUED_COOLDOWN_MS);
}

function getColumnHeader(key: string): string {
    if (key === 'processDate') return props.processDateHeader;
    return PAYMENTS_COLUMNS.find((column) => column.key === key)?.label ?? key;
}

function getProcessDate(row: PaymentRow): string {
    if (props.processDateMode === 'process-date') return row.processDate;
    return row.status === 'Cleared' ? row.createdOn : row.processDate;
}

function actionLabel(row: PaymentRow): string {
    if (props.actionMode === 'reject') return 'Reject';
    // PHASE2: if (props.actionMode === 'modify') return 'Modify';
    if (props.actionMode !== 'dynamic') return '';
    if (row.status === 'Pending' || row.status === 'Open') return 'Reject';
    // PHASE2: if (row.status === 'Low Balance') return 'Modify';
    return '';
}

function handleActionClick(row: PaymentRow) {
    const label = actionLabel(row);

    if (label === 'Reject') {
        selectedPaymentId.value = row.id;
        rejectModalVisible.value = true;
    }
    // PHASE2: else if (label === 'Modify') {
    // PHASE2:     modifyModalVisible.value = true;
    // PHASE2: }
}

function handleRejected(paymentId: number) {
    const row = rows.value.find((payment) => payment.id === paymentId);
    if (row) row.status = 'Canceled';
}

function handlePageChange(event: DataTablePageEvent) {
    if (event.rows !== limit.value) {
        fetchPayments(1, event.rows);
        return;
    }

    fetchPayments(event.page + 1, event.rows);
}

function handleSortChange(event: DataTableSortEvent) {
    if (event.sortOrder === 1 || event.sortOrder === -1) {
        sortField.value = event.sortField as string;
        sortOrder.value = event.sortOrder;
    } else {
        sortField.value = '';
        sortOrder.value = null;
    }

    fetchPayments(1, limit.value);
}

async function handleExportCurrentView() {
    if (
        exporting.value
        || exportCooldownActive.value
        || !columnPreferencesReady.value
        || columnPreferencesBusy.value
    ) return;

    // Lock immediately so rapid/double clicks cannot start concurrent exports.
    exporting.value = true;

    try {
        const columns = mapTableColumnsToExportColumns(displayedColumnKeys.value);
        if (columns.length === 0) {
            toast.add({
                severity: 'warn',
                summary: 'Export',
                detail: 'Select at least one table column to export.',
                life: 3000,
            });
            return;
        }

        const result = await exportPayments({
            ...getExportParams(),
            columns,
        });

        if (result.mode === 'queued') {
            exportQueuedMessage.value = result.message || DEFAULT_QUEUED_EXPORT_MESSAGE;
            exportQueuedModalVisible.value = true;
            startExportCooldown();
        } else {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Payments CSV downloaded.',
                life: 3000,
            });
        }
    } catch (error) {
        const apiError = ApiError.from(error);
        const data = apiError?.data as { message?: string } | undefined;
        let detail = 'Unable to export payments. Please try again.';
        if (apiError?.status === 429) {
            detail = 'Too many export requests. Please wait and try again.';
        } else if (typeof data?.message === 'string') {
            detail = data.message;
        }
        toast.add({
            severity: 'error',
            summary: 'Export failed',
            detail,
            life: 4000,
        });
    } finally {
        exporting.value = false;
    }
}

function handleColumnPersistenceError(action: 'save' | 'reset', error: unknown) {
    const apiError = ApiError.from(error);
    const data = apiError?.data as { message?: string } | undefined;
    const fallback = action === 'save'
        ? 'Unable to save column preferences. Please try again.'
        : 'Unable to reset column preferences. Please try again.';

    toast.add({
        severity: 'error',
        summary: action === 'save' ? 'Save failed' : 'Reset failed',
        detail: typeof data?.message === 'string' ? data.message : fallback,
        life: 4000,
    });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(TabStatPills, {
            stats: _unref(stats),
            loading: !_unref(hasExactTotals)
          }, null, 8, ["stats", "loading"]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(TabSearch, {
              modelValue: _unref(search),
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(search) ? (search).value = $event : null))
            }, null, 8, ["modelValue"]),
            _createElementVNode("div", _hoisted_5, [
              (showBankFilter.value)
                ? (_openBlock(), _createBlock(PaymentBankFilter, {
                    key: 0,
                    modelValue: _unref(bankIds),
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(bankIds) ? (bankIds).value = $event : null))
                  }, null, 8, ["modelValue"]))
                : _createCommentVNode("", true),
              (showTrustedAccountFilter.value)
                ? (_openBlock(), _createBlock(PaymentTrustedAccountFilter, {
                    key: 1,
                    modelValue: _unref(payeeIds),
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(payeeIds) ? (payeeIds).value = $event : null))
                  }, null, 8, ["modelValue"]))
                : _createCommentVNode("", true),
              (showStatusFilter.value)
                ? (_openBlock(), _createBlock(CheckboxFilter, {
                    key: 2,
                    modelValue: selectedStatusLabels.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedStatusLabels).value = $event)),
                    label: "Filter by Status",
                    options: statusOptionLabels.value
                  }, null, 8, ["modelValue", "options"]))
                : _createCommentVNode("", true),
              _createVNode(DateRangePicker, {
                modelValue: _unref(dateRange),
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(dateRange) ? (dateRange).value = $event : null)),
                label: "Filter by Date:",
                "trigger-width": "auto",
                "trigger-radius": "10px"
              }, null, 8, ["modelValue"])
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(ToolbarIconButton, {
            ariaLabel: "Export payments CSV",
            "data-product-tour": "payments-export",
            disabled: exporting.value || exportCooldownActive.value
                        || !columnPreferencesReady.value || columnPreferencesBusy.value,
            onClick: handleExportCurrentView
          }, {
            default: _withCtx(() => [
              _createVNode(IconExport)
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(ColumnCustomizeControl, {
            columns: _unref(PAYMENTS_COLUMNS),
            "locked-column-keys": _unref(PAYMENTS_LOCKED_COLUMN_KEYS),
            "disabled-column-keys": _unref(disabledColumnKeys),
            "visible-column-keys": _unref(visibleColumnKeys),
            "onUpdate:visibleColumnKeys": _cache[5] || (_cache[5] = ($event: any) => (_isRef(visibleColumnKeys) ? (visibleColumnKeys).value = $event : null)),
            "column-order": _unref(columnOrder),
            "onUpdate:columnOrder": _cache[6] || (_cache[6] = ($event: any) => (_isRef(columnOrder) ? (columnOrder).value = $event : null)),
            "table-id": _unref(TableColumnPreferenceId).PAYMENTS,
            persistence: "account",
            disabled: !columnPreferencesReady.value,
            onPersistenceError: handleColumnPersistenceError
          }, null, 8, ["columns", "locked-column-keys", "disabled-column-keys", "visible-column-keys", "column-order", "table-id", "disabled"])
        ])
      ])
    ]),
    _createVNode(AppTable, {
      ref_key: "tableRef",
      ref: tableRef,
      class: "payments-tab__table",
      value: _unref(rows),
      "data-key": "id",
      loading: _unref(loading),
      "paginator-loading": !_unref(hasExactTotals),
      lazy: true,
      rows: _unref(limit),
      first: (_unref(page) - 1) * _unref(limit),
      "total-records": _unref(totalRecords),
      "sort-field": _unref(sortField) || undefined,
      "sort-order": _unref(sortOrder),
      onPage: handlePageChange,
      onSort: handleSortChange
    }, {
      empty: _withCtx(() => [
        _createVNode(PaymentsEmptyState, {
          loading: _unref(loading),
          description: props.emptyDescription
        }, null, 8, ["loading", "description"])
      ]),
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedColumnKeys.value, (key) => {
          return (_openBlock(), _createBlock(_unref(Column), _mergeProps({
            key: key,
            field: key,
            header: getColumnHeader(key),
            sortable: _unref(PAYMENTS_SORTABLE_KEYS).has(key)
          }, { ref_for: true }, (key === 'amount' || key === 'processDate') ? _unref(col)({ align: 'right' }) : {}), {
            body: _withCtx(({ data }) => [
              (key === 'ssn' || key === 'coDebtorSsn')
                ? (_openBlock(), _createBlock(SsnDisplay, {
                    key: 0,
                    ssn: data[key]
                  }, null, 8, ["ssn"]))
                : (key === 'processDate')
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createTextVNode(_toDisplayString(_unref(formatDisplayDate)(getProcessDate(data))), 1)
                    ], 64))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                      _createTextVNode(_toDisplayString(data[key]), 1)
                    ], 64))
            ]),
            _: 2
          }, 1040, ["field", "header", "sortable"]))
        }), 128)),
        _createVNode(_unref(Column), _mergeProps({ header: "" }, _unref(col)({ fill: true })), null, 16),
        (props.actionMode !== 'none')
          ? (_openBlock(), _createBlock(_unref(Column), _mergeProps({
              key: 0,
              class: "payments-tab__action-col",
              header: ""
            }, _unref(col)({ align: 'right', fill: true })), {
              body: _withCtx(({ data }) => [
                (actionLabel(data))
                  ? (_openBlock(), _createBlock(_unref(Button), {
                      key: 0,
                      label: actionLabel(data),
                      variant: "outlined",
                      class: _normalizeClass(["payments-tab__action-btn", `payments-tab__action-btn--`
                        + `${actionLabel(data).toLowerCase()}`]),
                      onClick: ($event: any) => (handleActionClick(data))
                    }, null, 8, ["label", "class", "onClick"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 16))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["value", "loading", "paginator-loading", "rows", "first", "total-records", "sort-field", "sort-order"]),
    _createVNode(RejectPaymentModal, {
      visible: rejectModalVisible.value,
      "onUpdate:visible": _cache[7] || (_cache[7] = ($event: any) => ((rejectModalVisible).value = $event)),
      "payment-id": selectedPaymentId.value,
      onRejected: handleRejected
    }, null, 8, ["visible", "payment-id"]),
    _createVNode(QueuedExportModal, {
      visible: exportQueuedModalVisible.value,
      "onUpdate:visible": _cache[8] || (_cache[8] = ($event: any) => ((exportQueuedModalVisible).value = $event)),
      title: exportQueuedTitle.value,
      message: exportQueuedMessage.value
    }, null, 8, ["visible", "title", "message"])
  ], 64))
}
}

})